import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";

import Icon from "./Icon";
import UserAvatar from "./UserAvatar";
import Currency, { ConvertCurrency } from "../helpers/Currency/Currency";
import Badge from "./Badge";
import BackToAdmin from "../helpers/BackToAdmin/BackToAdmin";
import {
  convertNotificationTypeToColor,
  convertNotificationTypeToIcon,
  convertRoleToPath,
  convertRoleToText,
  convertTicketTypeToColor,
  convertTicketTypeToText,
  convertTransactionTypeToColor,
  convertTransactionTypeToIcon,
} from "../utils/enum";
import { convertDate } from "../utils";

const Header = ({ sidebarControl, sidebarActive, mobileMenuControl }) => {
  const title = useSelector((state) => state.title);

  return (
    <div className="navbar-header">
      <div className="row align-items-center justify-content-between">
        <div className="col-auto">
          <div className="d-flex flex-wrap align-items-center gap-3">
            <button
              type="button"
              className="sidebar-toggle"
              onClick={sidebarControl}
            >
              <Icon
                icon={
                  sidebarActive
                    ? "solar:close-circle-broken"
                    : "solar:hamburger-menu-broken"
                }
                className="icon text-2xxl non-active"
              />
            </button>
            <button
              onClick={mobileMenuControl}
              type="button"
              className="sidebar-mobile-toggle"
            >
              <Icon
                icon="solar:hamburger-menu-broken"
                className="icon text-2xxl non-active"
              />
            </button>
            <span className="text-xl fw-bold text-nowrap text-line-1 d-none d-sm-block">
              {title.title}
            </span>
          </div>
        </div>
        <div className="col-auto me-auto">
          <HeaderOperator />
        </div>
      </div>
    </div>
  );
};

export default Header;

export const HeaderOperator = () => {
  const permissions = useSelector((state) => state.permissions);

  return (
    <div className="d-flex flex-wrap align-items-center gap-3">
      <BackToAdmin />
      <ThemeToggleButton />
      {permissions.canTickets && <TicketDropdown />}
      <NotificationDropdown />
      {permissions.canTransactions && <TransactionDropdown />}
      <ProfileDropdown />
    </div>
  );
};

export const ThemeToggleButton = ({ isSwitch }) => {
  // 1. Initialize state for the current theme
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");

  // 2. Function to update the theme on the HTML element
  const updateThemeOnHtmlEl = (theme) => {
    document.documentElement.setAttribute("data-theme", theme);
  };

  // 4. On initial render, set the theme from localStorage
  useEffect(() => {
    updateThemeOnHtmlEl(theme);
  }, [theme]);

  // 5. Toggle theme when button is clicked
  const handleThemeToggle = () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
    updateThemeOnHtmlEl(newTheme);
  };

  if (isSwitch)
    return (
      <div className="form-switch switch-primary py-12 px-16 border radius-8 position-relative mb-16">
        <label
          htmlFor="theme"
          className="position-absolute w-100 h-100 start-0 top-0"
        />
        <div className="d-flex align-items-center gap-3 justify-content-between">
          <span className="form-check-label line-height-1 fw-medium text-secondary-light">
            حالت تاریک
          </span>
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id="theme"
            checked={theme === "dark"}
            onChange={handleThemeToggle}
          />
        </div>
      </div>
    );
  return (
    <button
      type="button"
      data-theme-toggle
      className="w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center"
      onClick={handleThemeToggle}
    >
      <Icon icon="solar:sun-broken" className="text-primary-light text-xl" />
    </button>
  );
};

const TicketDropdown = () => {
  const auth = useSelector((state) => state.auth);
  const tickets = useSelector((state) => state.meta?.tickets);

  const numberOfTickets = tickets?.length || 0;

  return (
    <div className="dropdown">
      <button
        className="has-indicator w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center"
        type="button"
        data-bs-toggle="dropdown"
      >
        <Icon
          icon="solar:letter-broken"
          className="text-primary-light text-xl"
        />
        {numberOfTickets > 0 && (
          <span
            className="position-absolute translate-middle-y rounded-pill bg-info-600 border-1 border-white w-8-px h-8-px"
            style={{ right: 10, top: 12 }}
          />
        )}
      </button>
      <div className="dropdown-menu to-top dropdown-menu-lg p-0">
        <div className="m-16 py-12 px-16 radius-8 bg-primary-50 mb-16 d-flex align-items-center justify-content-between gap-2">
          <div>
            <h6 className="text-lg text-primary-light fw-semibold mb-0">
              پشتیبانی
            </h6>
          </div>
          <span className="text-primary-600 fw-semibold text-lg w-40-px h-40-px rounded-circle bg-base d-flex justify-content-center align-items-center">
            {numberOfTickets}
          </span>
        </div>
        <div className="max-h-400-px overflow-y-auto scroll-sm pe-4">
          {tickets?.map((item) => (
            <Link
              to={`/${convertRoleToPath(auth.role)}/tickets/${item._id}`}
              className="px-24 py-12 d-flex align-items-start gap-3 mb-2 justify-content-between"
            >
              <div className="text-black d-flex align-items-center gap-3">
                <span className="w-40-px h-40-px rounded-circle flex-shrink-0 position-relative">
                  {auth?.isAdmin ? (
                    <UserAvatar
                      {...(item?.user?.avatar
                        ? { image: item?.user?.avatar }
                        : {
                            icon: "solar:user-broken",
                          })}
                    />
                  ) : (
                    <UserAvatar icon="solar:chat-square-call-broken" />
                  )}
                </span>
                <div className="text-start">
                  <h6 className="text-md fw-semibold mb-4 text-nowrap text-line-1">
                    {auth?.isAdmin ? (
                      <>
                        {item?.user?.firstName} {item?.user?.lastName}
                      </>
                    ) : (
                      item.title
                    )}
                  </h6>
                  <p className="mb-0 text-xs text-nowrap text-line-1">
                    {item.trackingCode}
                  </p>
                </div>
              </div>
              <div className="d-flex flex-column align-items-end">
                <span className="text-sm text-secondary-light flex-shrink-0 text-nowrap">
                  {convertDate(item.updatedAt, "DD MMM - HH:mm")}
                </span>

                <Badge color={convertTicketTypeToColor(item.status)}>
                  {convertTicketTypeToText(item.status)}
                </Badge>
              </div>
            </Link>
          ))}
        </div>
        <div className="text-center py-12 px-16">
          <Link
            to={`/${convertRoleToPath(auth.role)}/tickets`}
            className="text-primary-600 fw-semibold text-md"
          >
            نمایش همه
          </Link>
        </div>
      </div>
    </div>
  );
};

const NotificationDropdown = () => {
  const auth = useSelector((state) => state.auth);
  const notifications = useSelector((state) => state.meta?.notifications);

  const numberOfNotifications = notifications?.length || 0;

  return (
    <div className="dropdown">
      <button
        className="position-relative has-indicator w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center"
        type="button"
        data-bs-toggle="dropdown"
      >
        <Icon icon="solar:bell-broken" className="text-primary-light text-xl" />
        {numberOfNotifications > 0 && (
          <span
            className="position-absolute translate-middle-y rounded-pill bg-info-600 border-1 border-white w-8-px h-8-px"
            style={{ right: 10, top: 12 }}
          />
        )}
      </button>
      <div className="dropdown-menu to-top dropdown-menu-lg p-0">
        <div className="m-16 py-12 px-16 radius-8 bg-primary-50 mb-16 d-flex align-items-center justify-content-between gap-2">
          <div>
            <h6 className="text-lg text-primary-light fw-semibold mb-0">
              اعلان ها
            </h6>
          </div>
          <span className="text-primary-600 fw-semibold text-lg w-40-px h-40-px rounded-circle bg-base d-flex justify-content-center align-items-center">
            {numberOfNotifications}
          </span>
        </div>
        <div className="max-h-400-px overflow-y-auto scroll-sm">
          {notifications?.map((item) => {
            const color = convertNotificationTypeToColor(item.type);

            return (
              <div className="px-24 py-12 d-flex align-items-start gap-3 mb-2 justify-content-between text-start">
                <div className="text-black hover-bg-transparent hover-text-primary d-flex align-items-center gap-3">
                  <span
                    className={`w-44-px h-44-px bg-${color}-subtle text-${color}-main rounded-circle d-flex justify-content-center align-items-center flex-shrink-0`}
                  >
                    <Icon
                      icon={convertNotificationTypeToIcon(item.type)}
                      className="icon text-xxl"
                    />
                  </span>
                  <div>
                    <h6 className="text-md fw-semibold mb-4 text-nowrap text-line-1">
                      {item?.title}
                    </h6>
                    <p className="mb-0 text-sm text-secondary-light text-w-200-px text-nowrap text-line-1">
                      {item?.description}
                    </p>
                  </div>
                </div>
                <span className="text-sm text-secondary-light flex-shrink-0 text-end text-nowrap">
                  {convertDate(item?.createdAt, "jYYYY/jMM/jDD")}
                  <br />
                  {convertDate(item?.createdAt, "HH:mm:ss")}
                </span>
              </div>
            );
          })}
        </div>
        <div className="text-center py-12 px-16">
          <Link
            to={`/${convertRoleToPath(auth.role)}/notifications`}
            className="text-primary-600 fw-semibold text-md"
          >
            نمایش همه
          </Link>
        </div>
      </div>
    </div>
  );
};

const ProfileDropdown = () => {
  const user = useSelector((state) => state.user);

  return (
    <div className="dropdown">
      <button
        className="d-flex justify-content-center align-items-center rounded-circle"
        type="button"
        data-bs-toggle="dropdown"
      >
        <UserAvatar
          {...(user?.avatar
            ? { image: user?.avatar }
            : {
                icon: "solar:user-broken",
              })}
        />
      </button>
      <div className="dropdown-menu to-top dropdown-menu-sm">
        <div className="py-12 px-16 radius-8 bg-primary-50 mb-16 d-flex align-items-center justify-content-between gap-2">
          <div className="text-start">
            <h6 className="text-lg text-primary-light fw-semibold mb-2">
              {user.firstName} {user.lastName}
            </h6>
            <span className="text-secondary-light fw-medium text-sm">
              {convertRoleToText(user?.role)}
            </span>
          </div>
          <div>
            <UserAvatar
              {...(user?.avatar
                ? { image: user?.avatar }
                : {
                    icon: "solar:user-broken",
                  })}
            />
          </div>
        </div>
        <hr />
        <ul className="to-top-list">
          <li>
            <Link
              className="dropdown-item text-black p-8 hover-bg-transparent hover-text-primary d-flex align-items-center justify-content-between"
              to={`/${convertRoleToPath(user?.role)}/transactions`}
            >
              <span className="d-flex align-items-center gap-3">
                <Icon icon="solar:wallet-broken" className="icon text-xl" />
                موجودی
              </span>
              <Badge color="info">
                <ConvertCurrency price={user.wallet} />{" "}
                <Currency className="text-sm" isNormal />
              </Badge>
            </Link>
          </li>
          <li>
            <Link
              className="dropdown-item text-black p-8 hover-bg-transparent hover-text-primary d-flex align-items-center gap-3"
              to={`/${convertRoleToPath(user?.role)}/profile`}
            >
              <Icon icon="solar:user-broken" className="icon text-xl" />
              ویرایش حساب کاربری
            </Link>
          </li>
          <li>
            <Link
              className="dropdown-item text-black p-8 hover-bg-transparent hover-text-primary d-flex align-items-center gap-3"
              to={`/${convertRoleToPath(user?.role)}/profile/changed-password`}
            >
              <Icon
                icon="solar:pen-new-square-broken"
                className="icon text-xl"
              />
              ویرایش کلمه عبور
            </Link>
          </li>
          <li>
            <Link
              className="dropdown-item text-black p-8 hover-bg-transparent hover-text-primary d-flex align-items-center gap-3"
              to={`/${convertRoleToPath(user?.role)}/profile/settings`}
            >
              <Icon icon="solar:settings-broken" className="icon text-xl" />
              تنظیمات حساب کاربری
            </Link>
          </li>
          <li>
            <Link
              className="dropdown-item text-black p-8 border border-danger-200 bg-danger-50 bg-hover-danger-100 radius-12 d-flex align-items-center gap-3"
              to="/auth/logout"
            >
              <Icon icon="solar:logout-3-broken" className="icon text-xl" />{" "}
              خروج از حساب کاربری
            </Link>
          </li>
        </ul>
      </div>
    </div>
  );
};

const TransactionDropdown = () => {
  const auth = useSelector((state) => state.auth);
  const transactions = useSelector((state) => state.meta?.transactions);

  const numberOfTransactions = transactions?.length || 0;

  return (
    <div className="dropdown d-none d-sm-block">
      <button
        className="has-indicator w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center"
        type="button"
        data-bs-toggle="dropdown"
      >
        <Icon
          icon="solar:card-transfer-broken"
          className="text-primary-light text-xl"
        />
      </button>

      <div className="dropdown-menu to-top dropdown-menu-lg p-0">
        <div className="m-16 py-12 px-16 radius-8 bg-primary-50 mb-16 d-flex align-items-center justify-content-between gap-2">
          <div>
            <h6 className="text-lg text-primary-light fw-semibold mb-0">
              تراکنشات آخر
            </h6>
          </div>
          <span className="text-primary-600 fw-semibold text-lg w-40-px h-40-px rounded-circle bg-base d-flex justify-content-center align-items-center">
            {numberOfTransactions}
          </span>
        </div>
        <div className="max-h-400-px overflow-y-auto scroll-sm">
          {transactions?.map((item) => {
            const color = convertTransactionTypeToColor(item.type);

            return (
              <div className="px-24 py-12 d-flex align-items-start gap-3 mb-2 justify-content-between text-start">
                <div className="text-black hover-bg-transparent hover-text-primary d-flex align-items-center gap-3">
                  <span
                    className={`w-44-px h-44-px bg-${color}-subtle text-${color}-main rounded-circle d-flex justify-content-center align-items-center flex-shrink-0`}
                  >
                    <i
                      className={`icon text-xxl ${convertTransactionTypeToIcon(
                        item.type
                      )}`}
                    />
                  </span>
                  <div>
                    <h6 className="text-md fw-semibold mb-4 text-nowrap text-line-1">
                      <ConvertCurrency price={item?.amount} /> <Currency />
                    </h6>
                    <p className="mb-0 text-sm text-secondary-light text-w-200-px text-nowrap text-line-1">
                      {auth.isAdmin
                        ? `${item?.user?.firstName} ${item?.user?.lastName}`
                        : item?.trackingCode}
                    </p>
                  </div>
                </div>
                <span className="text-sm text-secondary-light flex-shrink-0 text-end text-nowrap">
                  {convertDate(item?.createdAt, "jYYYY/jMM/jDD")}
                  <br />
                  {convertDate(item?.createdAt, "HH:mm:ss")}
                </span>
              </div>
            );
          })}
        </div>
        <div className="text-center py-12 px-16">
          <Link
            to={`/${convertRoleToPath(auth.role)}/transactions`}
            className="text-primary-600 fw-semibold text-md"
          >
            نمایش همه
          </Link>
        </div>
      </div>
    </div>
  );
};
